import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface SuperAdminContextValue {
  isSuperAdmin: boolean;
  loading: boolean;
}

const SuperAdminContext = createContext<SuperAdminContextValue>({
  isSuperAdmin: false,
  loading: true,
});

/** One RPC per signed-in user per tab — avoids 10+ parallel `is_super_admin` calls on layout mount. */
let cachedUserId: string | null = null;
let cachedIsSuperAdmin = false;
let inflightRpc: Promise<boolean> | null = null;

async function fetchSuperAdminFlag(): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;
  if (inflightRpc) return inflightRpc;
  inflightRpc = supabase
    .rpc('is_super_admin')
    .then(({ data, error }) => {
      inflightRpc = null;
      if (error) {
        console.error('Error checking super admin status:', error);
        return false;
      }
      return Boolean(data);
    })
    .catch((err) => {
      inflightRpc = null;
      console.error('Error checking super admin status:', err);
      return false;
    });
  return inflightRpc;
}

export function SuperAdminProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<SuperAdminContextValue>({
    isSuperAdmin: false,
    loading: true,
  });

  useEffect(() => {
    if (!user) {
      cachedUserId = null;
      cachedIsSuperAdmin = false;
      setState({ isSuperAdmin: false, loading: false });
      return;
    }

    if (!isSupabaseConfigured()) {
      setState({ isSuperAdmin: false, loading: false });
      return;
    }

    if (cachedUserId === user.id) {
      setState({ isSuperAdmin: cachedIsSuperAdmin, loading: false });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    void fetchSuperAdminFlag().then((isSuperAdmin) => {
      if (cancelled) return;
      cachedUserId = user.id;
      cachedIsSuperAdmin = isSuperAdmin;
      setState({ isSuperAdmin, loading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return <SuperAdminContext.Provider value={state}>{children}</SuperAdminContext.Provider>;
}

export function useSuperAdminContext(): SuperAdminContextValue {
  return useContext(SuperAdminContext);
}
